import React, { useState } from 'react';
import { Button, Group, Popover, Stack, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconChevronLeft, IconChevronRight, IconDownload } from '@tabler/icons-react';
import 'dayjs/locale/fr';

import { exportPlanningToExcel } from '../../services/export/exportPlanningToExcel';
import { toast } from '../../ui/toast';

export default function PlanningExportMenu({
  events,
  planningName,
  defaultPlanningName,
  userLabelById,
  locale,
  t,
  fullWidth = false,
}) {
  const [opened, setOpened] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = () => {
    if (!Array.isArray(events) || events.length === 0) {
      toast.show({ title: t('planning.export_empty_title'), message: t('planning.export_empty_message'), color: 'yellow' });
      return;
    }

    if (startDate && endDate && startDate > endDate) {
      toast.show({ title: t('planning.export_invalid_title'), message: t('planning.export_invalid_message'), color: 'red' });
      return;
    }

    setExporting(true);
    try {
      const result = exportPlanningToExcel({
        events,
        planningName,
        fallbackName: defaultPlanningName,
        userLabelById,
        locale,
        startDate,
        endDate,
        columnLabels: {
          date: t('planning.export.columns.date'),
          week: t('planning.export.columns.week'),
          weekday: t('planning.export.columns.weekday'),
          start: t('planning.export.columns.start'),
          end: t('planning.export.columns.end'),
          duration: t('planning.export.columns.duration'),
          effective: t('planning.export.columns.effective'),
          event: t('planning.export.columns.event'),
          description: t('planning.export.columns.description'),
          role: t('planning.export.columns.role'),
          assigned: t('planning.export.columns.assigned'),
        },
      });

      if (result.status === 'empty') {
        toast.show({ title: t('planning.export_empty_title'), message: t('planning.export_empty_message'), color: 'yellow' });
        return;
      }

      toast.show({ title: t('planning.export_success_title'), message: t('planning.export_success_message', { count: result.rows }), color: 'green' });
      setOpened(false);
    } catch (error) {
      console.error('Failed to export planning', error);
      toast.show({ title: t('planning.export_error_title'), message: t('planning.export_error_message'), color: 'red' });
    } finally {
      setExporting(false);
    }
  };

  const clearDates = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const localeCode = locale.startsWith('fr') ? 'fr' : 'en';

  const sharedPickerProps = {
    size: 'sm',
    locale: localeCode,
    popoverProps: { withinPortal: true, offset: 8 },
    styles: (theme) => ({
      input: { fontSize: theme.fontSizes.sm },
      label: { marginBottom: theme.spacing.xs / 2 },
    }),
    calendarProps: {
      size: 'sm',
      nextIcon: <IconChevronRight size={16} stroke={1.5} />,
      previousIcon: <IconChevronLeft size={16} stroke={1.5} />,
      styles: (theme) => ({
        calendarHeaderControl: {
          width: 30,
          height: 30,
          minWidth: 30,
        },
        calendarHeader: {
          paddingBottom: theme.spacing.xs,
        },
      }),
    },
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      trapFocus
      closeOnClickOutside={false}
      closeOnEscape={false}
      position="bottom-end"
      shadow="md"
    >
      <Popover.Target>
        <Button
          variant="light"
          leftSection={<IconDownload size={16} />}
          onClick={() => setOpened((prev) => !prev)}
          fullWidth={fullWidth}
        >
          {t('planning.export_excel')}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="sm" miw={260}>
          <div>
            <Text size="sm" mb={4} fw={600}>{t('planning.export_title')}</Text>
            <Text size="xs" c="dimmed">{t('planning.export_hint')}</Text>
          </div>
          <DatePickerInput
            {...sharedPickerProps}
            label={t('planning.export_start_label')}
            placeholder={t('planning.export_start_placeholder')}
            value={startDate}
            onChange={setStartDate}
            maxDate={endDate || undefined}
            clearable
          />
          <DatePickerInput
            {...sharedPickerProps}
            label={t('planning.export_end_label')}
            placeholder={t('planning.export_end_placeholder')}
            value={endDate}
            onChange={setEndDate}
            minDate={startDate || undefined}
            clearable
          />
          <Group justify="space-between" gap="xs">
            <Button variant="subtle" size="xs" onClick={clearDates} disabled={!startDate && !endDate}>
              {t('planning.export_clear_range')}
            </Button>
            <Button onClick={handleExport} loading={exporting}>
              {t('planning.export_confirm')}
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
